/**
 * Signal download shared by the analysis and preview endpoints.
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165).
 */
import { assert } from '@brainfocus/web/utils/assert';
import { Npyjs } from '@brainfocus/contrib/npyjs';
import { map, Observable } from 'rxjs';
import * as fflate from 'fflate';
import { fetchReader } from '@brainfocus/web/api/response';

/**
 * Microvolts are the explicit unit contract at the data API boundary (#542):
 * the Python serializers convert volts→µV exactly once before quantisation,
 * so every dequantised sample reaching the viewer is µV. This is the
 * loud-failure tripwire — if a stray *1e6 is ever added upstream (or the
 * conversion is dropped), the signal's amplitude leaves the µV plausibility
 * band and we throw here, instead of silently corrupting per-channel
 * sensitivity (#371), FFT power, or spike thresholds.
 *
 * The check is on the AGGREGATE (typical cross-channel median of |sample|),
 * not per channel. A real unit-contract violation is systematic — it scales
 * EVERY channel by 1e6 — so the typical channel leaves the band. Asserting
 * per channel instead would crash the viewer on the handful of non-EEG /
 * saturated contacts (Pleth, DC, ECG) that legitimately sit far outside the
 * band on real recordings — that contamination is #371 and out of scope
 * here. Flat (median 0) and non-finite channels are skipped (disconnected
 * contacts are common and not unit violations).
 */
export function assertUvMagnitude(channelData: Float32Array[], context: string): void {
    const UV_MIN = 1;
    const UV_MAX = 100_000;
    const MAX_SAMPLES = 2048;
    const channelMedians: number[] = [];
    channelData.forEach((channel) => {
        const n = channel.length;
        if (n === 0) return;
        const step = Math.max(1, Math.floor(n / MAX_SAMPLES));
        const abs: number[] = [];
        for (let j = 0; j < n; j += step) abs.push(Math.abs(channel[j]!));
        abs.sort((a, b) => a - b);
        const median = abs[Math.floor(abs.length / 2)]!;
        if (median === 0 || !Number.isFinite(median)) return;
        channelMedians.push(median);
    });
    if (channelMedians.length === 0) return;
    channelMedians.sort((a, b) => a - b);
    const typical = channelMedians[Math.floor(channelMedians.length / 2)]!;
    if (typical < UV_MIN || typical > UV_MAX) {
        throw new Error(
            `[${context}] typical channel median |amplitude| = ${typical} µV (across `
            + `${channelMedians.length} channels) is outside the plausible µV band `
            + `[${UV_MIN}, ${UV_MAX}] — the data API unit contract (#542) is violated `
            + `(likely a missing or duplicated volts→µV conversion upstream).`,
        );
    }
}

/**
 * Helper method to fetch and parse EEG data from a URL.
 */
export function fetchEegData(url: string): Observable<{ progress: number } | Float32Array[]> {
    return fetchReader('GET', url).pipe(
        map((v) => {
            if (v instanceof ArrayBuffer) {
                const unzipd = fflate.unzipSync(new Uint8Array(v));
                assert(unzipd['eeg.npy'], `Missing file: eeg.npy`);
                const npyjs = new Npyjs();
                const npy = npyjs.parse(unzipd['eeg.npy'].buffer);
                const rows = npy.shape[0] ?? 0;
                const columns = npy.shape[1] ?? 0;
                const data = new Array(rows).fill(null)
                    .map((_, i) => npy.data.subarray(i * columns, (i + 1) * columns));
                let channelData: Float32Array[];
                if (unzipd['ranges.json']) {
                    const ranges = JSON.parse(new TextDecoder().decode(unzipd['ranges.json']));
                    // Assumes data are in any uint data type when the flag is available
                    channelData = data.map((intRow, i) => {
                        const floatRow = new Float32Array(intRow.length);
                        const intRange = 2 ** (intRow.BYTES_PER_ELEMENT * 8);
                        const floatRange = ranges[i]!;
                        for (let j = 0; j < intRow.length; j++) {
                            floatRow[j] = intRow[j]! / intRange * (floatRange[1] - floatRange[0]) + floatRange[0];
                        }
                        return floatRow;
                    });
                } else {
                    channelData = data;
                }
                assertUvMagnitude(channelData, 'fetchEegData');
                return channelData;
            } else {
                return v;
            }
        }),
    );
}
